import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { getFitColor } from '../../lib/scoreUtils';

const DIM_LABELS = {
  skills: 'Skills', experience: 'Experience', domain: 'Domain', education: 'Education',
  leadership: 'Leadership', scope_scale: 'Scope/Scale', complexity: 'Complexity',
  business_impact: 'Business Impact', stakeholder: 'Stakeholder',
};

const ROLE_TYPE_LABELS = {
  technical: 'Technical / IC', sales: 'Sales / Revenue',
  leadership: 'Leadership / Exec', general: 'General',
};

function StatCard({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} style={{ background: '#FAF7F2', border: '1px solid #ECE7DE', borderRadius: 12, padding: '0.875rem 1rem' }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0076A3', fontFamily: 'Cabinet Grotesk', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginTop: '0.375rem' }}>{label}</div>
      {sub && <div style={{ fontSize: '0.625rem', color: '#94A3B8', marginTop: '0.125rem' }}>{sub}</div>}
    </div>
  );
}

// Per-job "Scoring Insights": engine config, score distribution, and recruiter
// feedback (agree/disagree rate + common corrections) to tune weights with data.
export function ScoringInsightsDialog({ open, onOpenChange, jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await jobsAPI.scoringInsights(jobId);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load scoring insights');
    } finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const fb = data?.feedback || {};
  const totalScored = data?.scored_count || 0;
  const sortedFits = useMemo(
    () => Object.entries(data?.fit_distribution || {}).sort((a, b) => b[1] - a[1]),
    [data]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="scoring-insights-dialog" style={{ background: '#fff', border: '1px solid #CBD5E1', borderRadius: 14, maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem' }}>Scoring Insights</DialogTitle>
          <DialogDescription style={{ fontSize: '0.75rem' }}>
            Fitment engine output and recruiter feedback for this job.
          </DialogDescription>
        </DialogHeader>

        {loading && <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>Loading…</div>}

        {!loading && data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Engine config */}
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Current scoring configuration</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.6875rem' }}>
                {data.config.role_type && (
                  <span data-testid="insight-role-type" title="Role type auto-detected from the JD — it selects the dimension weight preset."
                    style={{ padding: '0.2rem 0.55rem', borderRadius: 999, background: '#EDE9FE', color: '#F05000', fontWeight: 700 }}>
                    {ROLE_TYPE_LABELS[data.config.role_type] || data.config.role_type}
                  </span>
                )}
                {Object.entries(data.config.weights || {})
                  .filter(([, w]) => w > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, w]) => (
                    <span key={k} data-testid={`insight-weight-${k}`} style={{ padding: '0.2rem 0.55rem', borderRadius: 999, background: '#EEF2FF', color: '#3730A3', fontWeight: 600 }}>
                      {DIM_LABELS[k] || k} {Math.round((w || 0) * 100)}%
                    </span>
                ))}
                {data.config.custom_weights && <span style={{ padding: '0.2rem 0.55rem', borderRadius: 999, background: '#FEF3C7', color: '#B45309', fontWeight: 600 }}>custom weights</span>}
                {data.config.bias_blind_scoring && <span style={{ padding: '0.2rem 0.55rem', borderRadius: 999, background: '#F3E8FF', color: '#7E22CE', fontWeight: 600 }}>bias-blind</span>}
                {data.config.high_accuracy_scoring && <span style={{ padding: '0.2rem 0.55rem', borderRadius: 999, background: '#E0F2FE', color: '#005A80', fontWeight: 600 }}>high-accuracy</span>}
                {data.semantic?.avg_fit != null && (
                  <span data-testid="insight-semantic-fit"
                    title={`Document-level semantic fit averaged across ${data.semantic.count} scored candidate${data.semantic.count !== 1 ? 's' : ''}. Blended 10% into high-accuracy scores.`}
                    style={{ padding: '0.2rem 0.55rem', borderRadius: 999, background: '#ECFEFF', color: '#0E7490', fontWeight: 600, cursor: 'help' }}>
                    semantic fit avg {Math.round(data.semantic.avg_fit)}%
                  </span>
                )}
              </div>
            </div>

            {/* Engine quality — internal benchmark trust badge */}
            {data.benchmark && (
              <div data-testid="scoring-benchmark-badge" style={{ background: 'linear-gradient(180deg,#F0FDFA,#ECFEFF)', border: '1px solid #99F6E4', borderRadius: 12, padding: '0.875rem 1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.6rem' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0E7490" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0E7490' }}>Engine quality — internal benchmark</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                  {[
                    { label: 'Skill-alias accuracy', value: `${Math.round(data.benchmark.skill_alias_accuracy * 100)}%`,
                      sub: `up from ${Math.round(data.benchmark.skill_alias_accuracy_baseline * 100)}%` },
                    { label: 'Ranking top-1', value: `${Math.round(data.benchmark.ranking_top1 * 100)}%`, sub: `τ ${(data.benchmark.ranking_tau ?? 0).toFixed(2)}` },
                    { label: 'Consistency', value: data.benchmark.consistency ? '✓' : '✗', sub: 'same in → same out' },
                    { label: 'Taxonomy rescues', value: data.benchmark.taxonomy_rescued, sub: 'adjacent skills' },
                  ].map((m) => (
                    <div key={m.label} data-testid="benchmark-metric" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0E7490', fontFamily: 'Cabinet Grotesk', lineHeight: 1 }}>{m.value}</div>
                      <div style={{ fontSize: '0.625rem', fontWeight: 600, color: '#0F766E', marginTop: '0.3rem' }}>{m.label}</div>
                      <div style={{ fontSize: '0.5625rem', color: '#0D9488', marginTop: '0.1rem' }}>{m.sub}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: '0.5625rem', color: '#0F766E', marginTop: '0.6rem', opacity: 0.8 }}>
                  Measured on {(data.benchmark.cases?.skill_pairs || 0) + (data.benchmark.cases?.taxonomy_pairs || 0) + (data.benchmark.cases?.coverage_cases || 0)} labelled cases
                  {data.benchmark.generated_at ? ` · ${new Date(data.benchmark.generated_at).toLocaleDateString()}` : ''}
                  {data.benchmark.semantic_embeddings ? ' · semantic embeddings active' : ''}
                </div>
              </div>
            )}


            {/* Top stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.625rem' }}>
              <StatCard testid="insight-scored" label="Scored" value={totalScored} />
              <StatCard testid="insight-avg" label="Avg score" value={data.avg_score ?? '—'} />
              <StatCard testid="insight-feedback" label="Feedback" value={fb.total} sub={`${fb.agree} 👍 · ${fb.disagree} 👎`} />
              <StatCard testid="insight-agree-rate" label="Agree rate" value={fb.agree_rate != null ? `${fb.agree_rate}%` : '—'} sub={fb.total ? `${fb.total} rated` : 'no feedback yet'} />
            </div>

            {/* Fit distribution */}
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Fit distribution</div>
              {totalScored === 0 ? (
                <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>No scored candidates yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {sortedFits.map(([fit, count]) => (
                    <div key={fit} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ width: 90, fontSize: '0.6875rem', fontWeight: 600, color: getFitColor(fit) }}>{fit}</span>
                      <div style={{ flex: 1, background: '#F1F5F9', borderRadius: 999, height: 14, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round((count / totalScored) * 100)}%`, height: '100%', background: getFitColor(fit) }} />
                      </div>
                      <span style={{ width: 28, textAlign: 'right', fontSize: '0.6875rem', fontFamily: 'JetBrains Mono', color: '#475569' }}>{count}</span>
                    </div>
                  ))}
                  {data.needs_review_count > 0 && (
                    <div style={{ fontSize: '0.6875rem', color: '#B45309', marginTop: '0.25rem' }}>
                      ⚠ {data.needs_review_count} candidate{data.needs_review_count !== 1 ? 's' : ''} flagged for manual review
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Common corrections */}
            {(fb.common_corrections || []).length > 0 && (
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Common corrections (recruiter → AI disagreements)</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {fb.common_corrections.map((c) => (
                    <div key={c.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', padding: '0.35rem 0.6rem', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8 }}>
                      <span style={{ color: '#9A3412', fontWeight: 500 }}>{c.label}</span>
                      <span style={{ color: '#C2410C', fontWeight: 700 }}>×{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Disagreement detail */}
            {(fb.disagreements || []).length > 0 && (
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Disagreements ({fb.disagreements.length})</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {fb.disagreements.map((d) => (
                    <div key={d.candidate_id} data-testid="insight-disagreement"
                      onClick={() => { onOpenChange(false); navigate(`/candidates/${d.candidate_id}`); }}
                      style={{ cursor: 'pointer', padding: '0.5rem 0.7rem', border: '1px solid #ECE7DE', borderRadius: 8, fontSize: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 600, color: '#002A38' }}>{d.candidate}</span>
                        <span style={{ color: 'hsl(var(--muted-foreground))' }}>
                          AI: {d.ai_fit || '—'} ({d.ai_score ?? '—'}){d.corrected_fit ? ` → ${d.corrected_fit}` : ''}
                        </span>
                      </div>
                      {d.note && <div style={{ color: '#64748B', marginTop: '0.2rem' }}>“{d.note}” — {d.by}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {fb.total === 0 && (
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', textAlign: 'center', padding: '0.5rem' }}>
                No recruiter feedback yet. Rate candidates from their detail page to see accuracy trends here.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
